import { Html } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import {
	type PointerEvent as ReactPointerEvent,
	type RefObject,
	useEffect,
	useRef,
} from "react";
import type * as THREE from "three";
import type { Market } from "../../domain/location/types";
import { snapRotationDeg } from "../../domain/viewer/saunaRotation";
import type { SaunaDimensions } from "../../engines/types";
import { getT } from "../../i18n";
import { emitter } from "../../lib/eventBus";
import { RotationDragController } from "./RotationDragController";
import type { OrbitControlsRef } from "./SceneControls";

/** Height of the handle above the ground, roughly eaves height. */
const HANDLE_Y = 2.45;
/** How far past the corner the handle floats, so it never sits on the wall. */
const HANDLE_OUTSET_M = 0.55;

interface RotationDragAffordanceProps {
	dimensions: SaunaDimensions;
	market?: Market;
	rotationDeg: number;
	/** The rotated rig; the gesture writes its `rotation.y` directly. */
	rigRef: RefObject<THREE.Object3D | null>;
	active?: boolean;
	controlsRef?: RefObject<OrbitControlsRef | null>;
	onRotationChange?: (rotationDeg: number) => void;
}

const rotateIcon = (
	<svg
		aria-hidden="true"
		focusable="false"
		width="16"
		height="16"
		viewBox="0 0 16 16"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M3 8a5 5 0 1 1 1.9 3.92"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
		/>
		<path
			d="M3 4.6V8h3.4"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

/**
 * Grab-and-turn handle for the whole building (issue: whole-building rotation
 * with a snap every 90 deg).
 *
 * Rendered INSIDE the sauna rig, so the handle rides the same corner of the
 * building at every orientation instead of drifting around it. The gesture
 * itself writes to the rig imperatively — see `RotationDragController` — and
 * only the release reaches the store, so a continuous turn costs one undo
 * entry, not fifty.
 *
 * This is the discoverable, direct-manipulation half of the feature. The
 * keyboard/touch-friendly half is the ⟲/⟳ stepper in the viewer HUD; both
 * commit through the same snapped action.
 */
export function RotationDragAffordance({
	dimensions,
	market = "se",
	rotationDeg,
	rigRef,
	active = true,
	controlsRef,
	onRotationChange,
}: RotationDragAffordanceProps) {
	const camera = useThree((s) => s.camera);
	const gl = useThree((s) => s.gl);
	const invalidate = useThree((s) => s.invalidate);
	const controllerRef = useRef<RotationDragController | null>(null);
	const activePointerIdRef = useRef<number | null>(null);

	// Tear down an in-flight gesture if the handle unmounts mid-drag (section
	// mode toggled on, window placement started) — otherwise orbit stays
	// disabled and the measurement controller stays parked forever.
	useEffect(
		() => () => {
			if (!controllerRef.current) return;
			controllerRef.current.dispose();
			controllerRef.current = null;
			if (activePointerIdRef.current !== null) {
				try {
					gl.domElement.releasePointerCapture(activePointerIdRef.current);
				} catch {
					// already released
				}
				activePointerIdRef.current = null;
			}
			if (controlsRef?.current) {
				controlsRef.current.enabled = true;
			}
			emitter.emit("rotation:handleDrag", false);
		},
		[gl, controlsRef],
	);

	const interactive = typeof onRotationChange === "function";

	const startRotationDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
		const rig = rigRef.current;
		if (!interactive || !rig || controllerRef.current) return;
		event.stopPropagation();

		const canvas = gl.domElement;
		const { pointerId } = event;
		// Same choreography as the partition/opening pills: the gesture starts
		// on this HTML button but the controller listens on the canvas, so
		// capture there or a release over the button never reaches it.
		try {
			canvas.setPointerCapture(pointerId);
		} catch {
			// jsdom / unsupported — the canvas listeners still work.
		}
		activePointerIdRef.current = pointerId;
		if (controlsRef?.current) {
			controlsRef.current.enabled = false;
		}
		emitter.emit("rotation:handleDrag", true);

		const previousDeg = snapRotationDeg(rotationDeg);
		const finish = () => {
			try {
				canvas.releasePointerCapture(pointerId);
			} catch {
				// already released
			}
			activePointerIdRef.current = null;
			if (controlsRef?.current) {
				controlsRef.current.enabled = true;
			}
			controllerRef.current?.dispose();
			controllerRef.current = null;
			emitter.emit("rotation:handleDrag", false);
			invalidate();
		};

		const controller = new RotationDragController({
			rig,
			camera: camera as THREE.PerspectiveCamera,
			renderer: gl,
			rotationDeg: previousDeg,
			planeY: HANDLE_Y,
			onCommit: (nextDeg) => {
				// A tap that never moved commits the same angle — skip it so it
				// doesn't add a no-op undo entry.
				if (nextDeg !== previousDeg) {
					onRotationChange?.(nextDeg);
				}
				finish();
			},
			onCancel: finish,
			onDragUpdate: () => invalidate(),
		});
		controller.startDrag();
		controllerRef.current = controller;
		invalidate();
	};

	if (!active) return null;

	const t = getT(market);
	const position: [number, number, number] = [
		dimensions.width / 2 + HANDLE_OUTSET_M,
		HANDLE_Y,
		dimensions.length / 2 + HANDLE_OUTSET_M,
	];
	const label = t.viewer_rotationHandle;

	return (
		<Html
			position={position}
			center
			transform={false}
			pointerEvents={interactive ? "auto" : "none"}
			zIndexRange={[40, 0]}
		>
			{interactive ? (
				<button
					type="button"
					className="viewer-drag-affordance viewer-drag-affordance--rotation"
					aria-label={label}
					title={label}
					onPointerDown={startRotationDrag}
				>
					{rotateIcon}
					<span className="viewer-drag-affordance__label">
						{t.viewer_rotationTitle}
					</span>
				</button>
			) : (
				<div
					className="viewer-drag-affordance viewer-drag-affordance--rotation"
					aria-hidden="true"
				>
					{rotateIcon}
					<span className="viewer-drag-affordance__label">
						{t.viewer_rotationTitle}
					</span>
				</div>
			)}
		</Html>
	);
}
